// The Android platform copy (spec section 6.8, decisions 9 and 26): the value
// goes into its own SharedPreferences file, `CoursesKeySync.xml`, through
// @capacitor/preferences configured with a dedicated group, and the manifest's
// backup rules (android/app/src/main/res/xml/courses_key_sync_*.xml) name that
// file and nothing else. Auto Backup then carries it, end-to-end encrypted
// under the screen lock, to a reinstall or a new device on the same Google
// account. The value sits unwrapped in app-private storage on purpose: a
// Keystore wrapping is device-bound and would make the backup useless.
//
// Android 9 (API 28) is the floor, and the app's `minSdkVersion` in
// android/variables.gradle is what enforces it: `requireFlags="clientSideEncryption"`
// in the backup rules is read only from API 28, and an older Android would
// back the file up under Google's key instead of the screen lock. Nothing here
// checks the OS level, because no older Android can install the app; the
// adapter's spec holds the Gradle file to the floor instead. Eligibility means
// the file can be written and read back, which on the real plugin it always
// can; whether a screen lock is set and backup is on decides if Android will
// carry it, neither is readable without native code, and the rules refuse the
// backup when the device cannot encrypt it (decision 17). The nudge copy says
// the key lives only on this phone until the backup runs.
import { ref } from 'vue';
import { Preferences } from '@capacitor/preferences';
import { PLATFORM_COPY_PROBE_KEY, platformCopyKey } from './platform-copy';

/** The SharedPreferences group, so the file is `shared_prefs/CoursesKeySync.xml`. */
export const ANDROID_PREFERENCES_GROUP = 'CoursesKeySync';
/**
 * The first Android that honours `requireFlags="clientSideEncryption"`. The
 * app's `minSdkVersion` must not drop below it; the spec checks that it does not.
 */
export const ANDROID_MIN_SDK_FOR_BACKUP = 28;

/**
 * @param {object} [deps]
 * @param {typeof Preferences} [deps.preferences] the plugin, replaceable in specs
 * @returns {import('./platform-key-adapter').PlatformKeyAdapter}
 */
export function createAndroidPlatformAdapter({ preferences = Preferences } = {}) {
  const eligible = ref(false);

  /**
   * Before every call, not once: `configure` swaps the plugin's single
   * SharedPreferences instance, so any other caller configuring a group
   * would otherwise send the key into a file the backup rules do not name.
   */
  const configure = () => preferences.configure({ group: ANDROID_PREFERENCES_GROUP });

  /** Writes, reads back and removes a probe value; true when the file kept it. */
  const roundTrip = async () => {
    const token = `probe:${Date.now()}`;
    try {
      await preferences.set({ key: PLATFORM_COPY_PROBE_KEY, value: token });
      const { value } = await preferences.get({ key: PLATFORM_COPY_PROBE_KEY });
      return value === token;
    } finally {
      await preferences.remove({ key: PLATFORM_COPY_PROBE_KEY }).catch(() => {});
    }
  };

  return {
    kind: 'android',
    isEligible: () => eligible.value,
    async probe() {
      try {
        await configure();
        eligible.value = await roundTrip();
      } catch (error) {
        console.warn('[CoursesKeys] Android preferences probe failed:', error);
        eligible.value = false;
      }
      return eligible.value;
    },
    async put(memberId, copy, slot) {
      const key = platformCopyKey(memberId, slot);
      await configure();
      await preferences.set({ key, value: copy });
    },
    async get(memberId, slot) {
      const key = platformCopyKey(memberId, slot);
      await configure();
      const { value } = await preferences.get({ key });
      return value ?? null;
    },
    async remove(memberId, slot) {
      const key = platformCopyKey(memberId, slot);
      await configure();
      await preferences.remove({ key });
    },
  };
}
